import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from './toast-context';

export type View =
    | 'bookings'
    | 'clients'
    | 'services'
    | 'workers'
    | 'stats'
    | 'assistant'
    | 'payments'
    | 'configuration';
export const views: View[] = [
    'bookings',
    'clients',
    'services',
    'workers',
    'stats',
    'assistant',
    'payments',
    'configuration',
];

export type ParsedRoute = {
    basePath: string;
    view: View;
    modalSegments: string[];
};

export function parseRouteFromPath(pathname: string): ParsedRoute {
    const segments = pathname.split('/').filter(Boolean);
    const viewIndex = segments.findIndex((segment) => views.includes(segment as View));
    const view: View = viewIndex >= 0 ? (segments[viewIndex] as View) : 'bookings';
    const basePath = viewIndex > 0 ? '/' + segments.slice(0, viewIndex).join('/') : '';
    const modalSegments = viewIndex >= 0 ? segments.slice(viewIndex + 1) : [];
    return { basePath, view, modalSegments };
}

export function buildPath(basePath: string, view: View, modalSegments: string[]): string {
    const parts = [basePath, view, ...modalSegments].filter(Boolean).join('/');
    return parts.startsWith('/') ? parts : '/' + parts;
}

type ListModalConfig<T> = {
    isActive: boolean;
    ready: boolean;
    segments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    findById: (id: string) => T | undefined;
    label: string;
    singularLabel?: string;
    baseSegments?: string[];
};

type ListModalState<T> =
    | { kind: 'none'; item: null; id: null }
    | { kind: 'new'; item: null; id: null }
    | { kind: 'item'; item: T; id: string };

export function useListModalRoute<T>({
    isActive,
    ready,
    segments,
    onNavigate,
    findById,
    label,
    singularLabel,
    baseSegments = [],
}: ListModalConfig<T>): {
    state: ListModalState<T>;
    error: string | null;
    openNew: () => void;
    openItem: (id: string) => void;
    close: () => void;
} {
    const [state, setState] = useState<ListModalState<T>>({ kind: 'none', item: null, id: null });
    const [error, setError] = useState<string | null>(null);
    const baseKey = useMemo(() => baseSegments.join('|'), [baseSegments]);
    const { showToast } = useToast();

    // Helper to singularize label for "Not found" message if possible, or just use label
    const effectiveSingularLabel = singularLabel || (label.endsWith('s') ? label.slice(0, -1) : label);
    const capitalizedLabel = effectiveSingularLabel.charAt(0).toUpperCase() + effectiveSingularLabel.slice(1);

    useEffect(() => {
        if (!isActive || !ready) return;
        setError(null);
        if (segments.length === 0) {
            setState((prev) => (prev.kind === 'none' ? prev : { kind: 'none', item: null, id: null }));
            return;
        }
        const [first, ...rest] = segments;
        if (first === 'new') {
            if (rest.length > 0) {
                const msg = `Rota inválida para ${label}.`;
                setError(msg);
                showToast(msg);
                onNavigate(baseSegments, true);
                setState((prev) => (prev.kind === 'none' ? prev : { kind: 'none', item: null, id: null }));
                return;
            }
            setState((prev) => (prev.kind === 'new' ? prev : { kind: 'new', item: null, id: null }));
            return;
        }
        // An item route may carry further segments, addressing a modal nested inside it
        // that matches on the remainder; a path belonging to neither opens nothing.
        const found = findById(first);
        if (!found) {
            showToast(`${capitalizedLabel} não encontrado.`);
            onNavigate(baseSegments, true);
            setState((prev) => (prev.kind === 'none' ? prev : { kind: 'none', item: null, id: null }));
            return;
        }
        setState((prev) =>
            prev.kind === 'item' && prev.id === first && prev.item === found
                ? prev
                : { kind: 'item', item: found, id: first },
        );
    }, [segments, isActive, ready, onNavigate, baseKey, findById, label, baseSegments, showToast, capitalizedLabel]);

    const openNew = useCallback(() => onNavigate([...baseSegments, 'new']), [onNavigate, baseSegments]);
    const openItem = useCallback((id: string) => onNavigate([...baseSegments, id]), [onNavigate, baseSegments]);
    const close = useCallback(() => onNavigate(baseSegments, true), [onNavigate, baseSegments]);

    return { state, error, openNew, openItem, close };
}

type SingleModalConfig = {
    isActive: boolean;
    ready: boolean;
    segments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    modalKey: string;
    baseSegments?: string[];
};

export function useSingleModalRoute({
    isActive,
    ready,
    segments,
    onNavigate,
    modalKey,
    baseSegments = [],
}: SingleModalConfig): {
    isOpen: boolean;
    open: () => void;
    close: () => void;
} {
    const open = useCallback(() => onNavigate([...baseSegments, modalKey]), [onNavigate, baseSegments, modalKey]);
    const close = useCallback(() => onNavigate(baseSegments, true), [onNavigate, baseSegments]);

    // Anything past this modal's own segment addresses a modal nested inside it,
    // which matches on the remainder; a path belonging to neither simply opens
    // nothing, since no single modal can tell a typo from a sibling's route.
    const routeMatches = isActive && segments[0] === modalKey;

    // `ready` gates the initial open so a deep link doesn't render a modal with
    // no data. Once open, the modal stays open even if its data goes back to
    // loading — otherwise a refresh unmounts it and the backdrop flashes.
    const [hasOpened, setHasOpened] = useState(false);
    useEffect(() => {
        if (!routeMatches) setHasOpened(false);
        else if (ready) setHasOpened(true);
    }, [routeMatches, ready]);

    const isOpen = routeMatches && (ready || hasOpened);

    return { isOpen, open, close };
}
